// @flow
import download from "downloadjs";
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import {
  PartNode,
  DiscountsNode,
  QuoteNode,
  ProductSheetNode,
  LicenceNode
} from "../types/GraphQlNodes";
import { apiFailCallbackHandle } from "../containers/ErrosDisplay";
import {
  SET_PRICE_SHEET,
  UPDATE_QUOTE_LIST,
  GET_QUOTE_COMPANY_LIST,
  ADMIN_UPDATE_QUOTE_LIST,
  GET_PRODUCT_SHEET,
  GET_QUOTE_LIST,
  GET_QUOTE_LICENCES
} from "../../global/actions/actionNames";

const root = Properties.api_root;

export const fetchPriceSheet = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    product(name: "IMQ") {
      id
      name
      currentRevisionId
      description
      parts {
        edges {
          node {
            ${PartNode}
          }
        }
      }
      tiers {
        ${DiscountsNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: SET_PRICE_SHEET, data: json.data.product });
      callback && callback(json.data.product);
    });
};

export const CreateQuote = (
  retailSum: number,
  resellerSum: number,
  distributerSum: number,
  authorisedReseller: boolean,
  licenceType: string,
  parts: Array<*>,
  companyName: string,
  companyId: string,
  tierId: string,
  productSheetId: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: any): * => {
  const products = parts.map(
    (item: *): * => `{partNo : "${item.partNo}", quantity : ${item.quantity},
    quotedRetailPrice: ${item.quotedRetailPrice},
    quotedDistributerPrice: ${item.quotedDistributerPrice},
    quotedResellerPrice: ${item.quotedResellerPrice}}`
  );
  const graphQl = `
  mutation{
    createQuote(
      shownPrices: {retail: ${retailSum}, reseller: ${resellerSum}, distributer: ${distributerSum}},
      authorisedReseller: ${authorisedReseller},
      licenceType: ${licenceType},
      notes: "",
      products: {
        productId: "IMQ",
        parts: [${products}]
      },
      companyName: "${companyName}",
      companyId: "${companyId}",
      tierId: "${tierId}",
      productSheetId: "${productSheetId}"
    ){
      ok
      errors {
        ...on PriceMismatchError {
          errorType
          message
          priceType
          calculatedPrice
          displayedPrice
        }
        ...on TierMismatchError {
          message
          errorType
        }
        ...on ProductSheetNotFound {
          message
          errorType
        }
      }
      quote {
        ${QuoteNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createQuote;
        if (result.ok) {
          dispatch({ type: UPDATE_QUOTE_LIST, quote: result.quote });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const UpdateQuote = (
  quoteId: string,
  retailSum: number,
  resellerSum: number,
  distributerSum: number,
  authorisedReseller: boolean,
  parts: Array<*>,
  companyName: string,
  companyId: string,
  tierId: string,
  productSheetId: string,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin?: boolean = false
): * => (dispatch: any): * => {
  const products = parts.map(
    (item: *): * => `{id: "${item.id}", partNo : "${item.partNo}",
    quantity : ${item.quantity},
    quotedRetailPrice: ${item.quotedRetailPrice},
    quotedDistributerPrice: ${item.quotedDistributerPrice},
    quotedResellerPrice: ${item.quotedResellerPrice}}`
  );
  const graphQl = `
  mutation{
    updateQuote(
      quoteId: "${quoteId}",
      shownPrices: {retail: ${retailSum}, reseller: ${resellerSum}, distributer: ${distributerSum}},
      authorisedReseller: ${authorisedReseller},
      notes: "",
      parts: [${products}],
      companyName: "${companyName}",
      companyId: "${companyId}",
      tierId: "${tierId}",
      productSheetId: "${productSheetId}"
    ){
      ok
      errors {
        ...on PriceMismatchError {
          errorType
          message
          priceType
          calculatedPrice
          displayedPrice
        }
        ...on TierMismatchError {
          message
          errorType
        }
        ...on QuoteNotFound {
          message
          errorType
        }
        ...on NoCompanyError {
          message
          errorType
        }
        ...on PartNotFound {
          message
          errorType
        }
        ...on ProductSheetNotFound {
          message
          errorType
        }
      }
      quote {
        ${QuoteNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateQuote;
        if (result.ok) {
          if (isAdmin) {
            dispatch({ type: ADMIN_UPDATE_QUOTE_LIST, quote: result.quote });
          } else {
            dispatch({ type: UPDATE_QUOTE_LIST, quote: result.quote });
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const fetchQuoteList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    quotes {
      ${QuoteNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: GET_QUOTE_LIST, quotes: json.data.quotes });
      callback && callback(json.data.quotes);
    });
};

export const fetchCompanyList = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    children {
      id
      uuid
      name
      companyType
      inheritAuthority
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: GET_QUOTE_COMPANY_LIST, companies: json.data.children });
      callback && callback(json.data.children);
    });
};

export const fetchProductSheet = (): * => (dispatch: DispatchType): * => {
  const graphQl = `
  {
    productSheet {
      ${ProductSheetNode}
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({
        type: GET_PRODUCT_SHEET,
        productSheet: json.data.productSheet
      });
    });
};

export const fetchLongTermLicence = (callback?: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    licences(licenceType: LONGTERM) {
      ${LicenceNode}
      company{
        id
        uuid
        name
        companyType
        inheritAuthority
        inheritTier{
          ${DiscountsNode}
        }
        inheritProductSheet{
          ${ProductSheetNode}
        }
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      dispatch({ type: GET_QUOTE_LICENCES, licences: json.data.licences });
      callback && callback(json.data.licences);
    });
};

export const CreateUpdatingQuote = (
  retailSum: number,
  distributerSum: number,
  resellerSum: number,
  parts: Array<*>,
  licenceId: string,
  newStartDate: string,
  newExpiryDate: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: any): * => {
  const products = parts.map(
    (item: *): * => `{partNo : "${item.partNo}", quantity : ${item.quantity},
    quotedRetailPrice: ${item.quotedRetailPrice},
    quotedDistributerPrice: ${item.quotedDistributerPrice},
    quotedResellerPrice: ${item.quotedResellerPrice}}`
  );
  const graphQl = `
  mutation{
    createUpgradingQuote(
      shownPrices: {retail: ${retailSum}, reseller: ${resellerSum}, distributer: ${distributerSum}},
      products: {
        productId: "IMQ",
        parts: [${products}]
      },
      licenceId: "${licenceId}",
      newStartDate: "${newStartDate}",
      newExpiryDate: "${newExpiryDate}"
    ){
      ok
      errors {
        ...on PriceMismatchError {
          errorType
          message
          priceType
          calculatedPrice
          displayedPrice
        }
        ...on LicenceNotFound {
          message
          errorType
        }
        ...on InputDateMismatchError {
          message
          errorType
        }
      }
      quote {
        ${QuoteNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createUpgradingQuote;
        if (result.ok) {
          dispatch({ type: UPDATE_QUOTE_LIST, quote: result.quote });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const EditUpdatingQuote = (
  retailSum: number,
  distributerSum: number,
  resellerSum: number,
  parts: Array<*>,
  quoteId: string,
  newStartDate: string,
  newExpiryDate: string,
  doneCallback: Function,
  failCallBack: Function
): * => (dispatch: DispatchType): * => {
  const products = parts.map(
    (item: *): * => `{id: "${item.id}", partNo : "${item.partNo}",
    quantity : ${item.quantity},
    quotedRetailPrice: ${item.quotedRetailPrice},
    quotedDistributerPrice: ${item.quotedDistributerPrice},
    quotedResellerPrice: ${item.quotedResellerPrice}}`
  );

  const graphQl = `
  mutation{
    editUpgradingQuote(
      shownPrices: {retail: ${retailSum}, reseller: ${resellerSum}, distributer: ${distributerSum}},
      parts: [${products}],
      quoteId: "${quoteId}",
      newStartDate: "${newStartDate}",
      newExpiryDate: "${newExpiryDate}"
    ){
      ok
      errors {
        ...on PriceMismatchError {
          errorType
          message
          priceType
          calculatedPrice
          displayedPrice
        }
        ...on QuoteNotFound {
          message
          errorType
        }
        ...on PartNotFound {
          message
          errorType
        }
        ...on InputDateMismatchError {
          message
          errorType
        }
      }
      quote {
        ${QuoteNode}
      }
    }
  }
  `;
  return fetch(`${root}/auth/v2/billing/graphql`, graphQLInit(graphQl))
    .then(response => response.json())
    .then(json => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.editUpgradingQuote;
        if (result.ok) {
          dispatch({ type: UPDATE_QUOTE_LIST, quote: result.quote });
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch(err => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export function downloadQuoteData(
  quoteId: string,
  companyName: string,
  file_type: string,
  doneCall: Function,
  failBack: Function
): DispatchType {
  return (dispatch: DispatchType) => {
    fetch(
      root + "/auth/v2/billing/quotes/" + quoteId + "?file_type=" + file_type,
      getInit
    )
      .then(
        (response: Response): * => {
          if (response.ok) {
            return file_type === "excel" ? response.blob() : response.text();
          } else {
            return response.json();
          }
        }
      )
      .then(
        (json: *): * => {
          if (json.errors) {
            failBack && failBack(apiFailCallbackHandle(json.errors, "quote"));
            return Promise.reject();
          } else {
            return json;
          }
        }
      )
      .then(
        (blob: *): * => {
          if (file_type === "excel") {
            download(
              blob,
              `${companyName}-quote-${new Date(
                Date.now()
              ).toLocaleString()}.xlsx`,
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            );
          } else {
            download(
              blob,
              `${companyName}-quote-${new Date(
                Date.now()
              ).toLocaleString()}.csv`,
              "text/csv"
            );
          }
          doneCall && doneCall();
        }
      )
      .catch(err => {
        failBack && failBack(apiFailCallbackHandle(err.message));
      });
  };
}
